import React, { useState, useEffect } from "react";
import { Modal, Card, Table, Spinner, Alert, Pagination, Badge, Row, Col } from "react-bootstrap";
import { FaRobot, FaChartLine, FaClock, FaUser, FaComment, FaHashtag, FaInfoCircle } from "react-icons/fa";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { Icon } from "../../../components/common/Icon";
import apiClient from "../../../api/apiClient";
import "../../../styles/components/_chatbot-detail-modal.scss";

interface ChatbotUsageDetail {
  usage_id: number;
  message_id: number;
  session_id: string;
  token_count: number;
  model_name: string;
  operation_type: string;
  timestamp: string;
  user_message?: string;
  assistant_message?: string;
}

interface ChatbotUsageResponse {
  chatbot_id: number;
  chatbot_name: string;
  company_name: string;
  total_records: number;
  current_page: number;
  per_page: number;
  total_pages: number;
  period_start: string;
  period_end: string;
  chart_data: Array<{
    period: string;
    tokens: number;
    cost: number;
  }>;
  usage_records: ChatbotUsageDetail[];
}

interface ChatbotDetailModalProps {
  show: boolean;
  onHide: () => void;
  chatbotId: number;
  chatbotName: string;
  startDate: string;
  endDate: string;
}

const ChatbotDetailModal: React.FC<ChatbotDetailModalProps> = ({
  show,
  onHide,
  chatbotId,
  chatbotName,
  startDate,
  endDate,
}) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usageData, setUsageData] = useState<ChatbotUsageResponse | null>(null);

  const loadUsageData = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(
        `/usage/chatbots/${chatbotId}/usage`,
        {
          params: {
            page,
            per_page: 10,
            start_date: startDate,
            end_date: endDate,
          },
        }
      );

      setUsageData(response);
      setCurrentPage(page);
    } catch (err: any) {
      setError(err.response?.data?.detail || t("failed_to_load_usage_data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && chatbotId) {
      loadUsageData(1);
    }
  }, [show, chatbotId, startDate, endDate]);

  const handlePageChange = (page: number) => {
    loadUsageData(page);
  };

  const getChartConfig = () => {
    if (!usageData?.chart_data || usageData.chart_data.length === 0) {
      return null;
    }

    const days_diff = Math.ceil(
      (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)
    );

    const labels = usageData.chart_data.map((item) => {
      if (days_diff <= 3) {
        // Hourly format: "Dec 15 14:00"
        return new Date(item.period).toLocaleDateString(i18n.language, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        // Daily format: "Dec 15"
        return new Date(item.period).toLocaleDateString(i18n.language, {
          month: 'short',
          day: 'numeric'
        });
      }
    });

    return {
      labels,
      datasets: [
        {
          label: t("tokens_used"),
          data: usageData.chart_data.map((item) => item.tokens),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y',
        },
        {
          label: t("cost_usd"),
          data: usageData.chart_data.map((item) => item.cost),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y1',
        },
      ],
    };
  };

  const formatDMY = (dateStr: string) => {
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const formatDate = (dateString: string) => {
    // For timestamps with time, keep time but format date as dd-mm-YYYY
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
  };

  const formatMessage = (message?: string) => {
    if (!message) return "N/A";
    return message.length > 100 ? message.substring(0, 100) + "..." : message;
  };

  const days_diff = Math.ceil(
    (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)
  );

  const chartTitle = days_diff <= 3 ? t("hourly_usage_breakdown") : t("daily_usage_breakdown");

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable className="chatbot-detail-modal">
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="w-100">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <span className="modal-icon">
                <Icon icon={FaRobot} />
              </span>
              <div>
                <div className="modal-title-text">{chatbotName}</div>
                <div className="modal-subtitle">{t("detailed_token_usage_and_messages")}</div>
              </div>
            </div>
            <div className="text-end">
              <div className="badge bg-light text-dark me-2">{usageData?.company_name || ""}</div>
              <div className="badge bg-primary">{usageData?.total_records?.toLocaleString() || 0} {t("records")}</div>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-0">
        {/* Period summary */}
        <div className="period-bar">
          <div className="d-flex align-items-center gap-3">
            <span className="period-chip">
              <Icon icon={FaClock} className="me-1" /> {t("start")}: <strong>{formatDMY(startDate)}</strong>
            </span>
            <span className="period-chip">
              <Icon icon={FaClock} className="me-1" /> {t("end")}: <strong>{formatDMY(endDate)}</strong>
            </span>
            <span className="info-chip">
              <Icon icon={FaInfoCircle} className="me-1" /> {chartTitle}
            </span>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && !usageData && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">{t("loading_chatbot_usage_data")}</p>
          </div>
        )}

        {usageData && (
          <>
            {/* Summary Stats */}
            <Row className="mb-4">
              <Col md={3}>
                <Card className="text-center h-100">
                  <Card.Body>
                    <Icon icon={FaChartLine} size={24} className="text-primary mb-2" />
                    <h5>{usageData.total_records.toLocaleString()}</h5>
                    <small className="text-muted">{t("total_records")}</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center h-100">
                  <Card.Body>
                    <Icon icon={FaRobot} size={24} className="text-success mb-2" />
                    <h5>{usageData.chatbot_name}</h5>
                    <small className="text-muted">{t("chatbot")}</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center h-100">
                  <Card.Body>
                    <Icon icon={FaClock} size={24} className="text-info mb-2" />
                    <h5>{formatDMY(usageData.period_start)}</h5>
                    <small className="text-muted">{t("start")}</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center h-100">
                  <Card.Body>
                    <Icon icon={FaClock} size={24} className="text-warning mb-2" />
                    <h5>{formatDMY(usageData.period_end)}</h5>
                    <small className="text-muted">{t("end")}</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Chart */}
            {usageData.chart_data && usageData.chart_data.length > 0 && (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">{chartTitle}</h5>
                </Card.Header>
                <Card.Body style={{ height: "300px" }}>
                  <Line
                    data={getChartConfig()!}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: {
                        mode: 'index',
                        intersect: false,
                      },
                      plugins: {
                        legend: {
                          position: 'top',
                        },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                        },
                      },
                      scales: {
                        x: {
                          display: true,
                          title: {
                            display: true,
                            text: days_diff <= 3 ? t("time_hourly") : t("date_daily")
                          }
                        },
                        y: {
                          type: 'linear',
                          display: true,
                          position: 'left',
                          title: {
                            display: true,
                            text: t("tokens")
                          }
                        },
                        y1: {
                          type: 'linear',
                          display: true,
                          position: 'right',
                          title: {
                            display: true,
                            text: t("cost_usd")
                          },
                          grid: {
                            drawOnChartArea: false,
                          },
                        },
                      },
                    }}
                  />
                </Card.Body>
              </Card>
            )}

            {/* Usage Records Table */}
            <Card className="records-card">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{t("usage_records")}</h5>
                  <Badge bg="info">
                    {t("page")} {usageData.current_page} {t("of")} {usageData.total_pages}
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {usageData.usage_records && usageData.usage_records.length > 0 ? (
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '160px' }}>{t("timestamp")}</th>
                        <th style={{ width: '120px' }}>{t("session")}</th>
                        <th style={{ width: '100px' }}>{t("tokens")}</th>
                        <th style={{ width: '110px' }}>{t("model")}</th>
                        <th style={{ width: '120px' }}>{t("operation")}</th>
                        <th>{t("user_message")}</th>
                        <th>{t("assistant_response")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageData.usage_records.map((record) => (
                        <tr key={record.usage_id}>
                          <td>
                            <small>{formatDate(record.timestamp)}</small>
                          </td>
                          <td>
                            <span className="mono-pill">
                              <Icon icon={FaHashtag} className="me-1" />
                              {record.session_id.substring(0, 6)}
                            </span>
                          </td>
                          <td>
                            <Badge bg="primary">
                              {record.token_count.toLocaleString()}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="light" text="dark">{record.model_name}</Badge>
                          </td>
                          <td>
                            <Badge bg={record.operation_type === 'completion' ? 'success' : 'secondary'}>
                              {record.operation_type}
                            </Badge>
                          </td>
                          <td className="clip-cell">
                            <div className="d-flex align-items-start">
                              <Icon icon={FaUser} size={12} className="text-muted me-1 mt-1" />
                              <small className="text-wrap">
                                {formatMessage(record.user_message)}
                              </small>
                            </div>
                          </td>
                          <td className="clip-cell">
                            <div className="d-flex align-items-start">
                              <Icon icon={FaComment} size={12} className="text-muted me-1 mt-1" />
                              <small className="text-wrap">
                                {formatMessage(record.assistant_message)}
                              </small>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-4">
                    <Icon icon={FaRobot} size={48} className="text-muted mb-3" />
                    <h5>{t("no_usage_records_found")}</h5>
                    <p className="text-muted">{t("no_usage_data_available_for_this_period")}</p>
                  </div>
                )}
              </Card.Body>

              {/* Pagination */}
              {usageData.total_pages > 1 && (
                <Card.Footer className="bg-white sticky-footer">
                  <div className="d-flex justify-content-center">
                    <Pagination>
                      <Pagination.First
                        disabled={currentPage === 1 || loading}
                        onClick={() => handlePageChange(1)}
                      />
                      <Pagination.Prev
                        disabled={currentPage === 1 || loading}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />

                      {/* Show page numbers */}
                      {Array.from({ length: Math.min(5, usageData.total_pages) }, (_, i) => {
                        let pageNum: number;
                        if (usageData.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= usageData.total_pages - 2) {
                          pageNum = usageData.total_pages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <Pagination.Item
                            key={pageNum}
                            active={pageNum === currentPage}
                            disabled={loading}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      })}

                      <Pagination.Next
                        disabled={currentPage === usageData.total_pages || loading}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      <Pagination.Last
                        disabled={currentPage === usageData.total_pages || loading}
                        onClick={() => handlePageChange(usageData.total_pages)}
                      />
                    </Pagination>
                  </div>
                </Card.Footer>
              )}
            </Card>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ChatbotDetailModal;
